import { type ButtonHTMLAttributes, type AnchorHTMLAttributes, forwardRef } from 'react'
import { Link } from 'react-router'

type ButtonVariant = 'primary' | 'secondary' | 'ghost'

type BaseProps = {
  variant?: ButtonVariant
  to?: string
  children: React.ReactNode
  className?: string
}

type AsButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & { to?: undefined }

type AsLink = BaseProps &
  Omit<AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & { to: string }

export type ButtonProps = AsButton | AsLink

const variantClasses: Record<ButtonVariant, string> = {
  primary:
    'bg-sage text-white hover:bg-sage-light hover:brightness-90',
  secondary:
    'border border-sage text-sage bg-transparent hover:bg-sage hover:text-white',
  ghost:
    'text-gray-600 hover:bg-gray-100',
}

const baseClasses =
  'inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-medium transition-all duration-150 ease-out active:scale-[0.97] active:duration-100 cursor-pointer disabled:opacity-50 disabled:pointer-events-none'

export const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  function Button({ variant = 'primary', to, className = '', children, ...rest }, ref) {
    const classes = `${baseClasses} ${variantClasses[variant]} ${className}`

    if (to) {
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          to={to}
          className={classes}
          {...(rest as Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className'>)}
        >
          {children}
        </Link>
      )
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        className={classes}
        {...(rest as Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'>)}
      >
        {children}
      </button>
    )
  }
)
